/**
 * Centralized Error Handling Utility for ACCTOO
 * Provides consistent error formatting and handling across the application
 */

export class ErrorHandler {
  // Firebase Auth Error Codes Mapping
  static FIREBASE_AUTH_ERRORS = {
    'auth/user-not-found': 'No account found with this email address',
    'auth/wrong-password': 'Incorrect password. Please try again',
    'auth/email-already-in-use': 'This email address is already registered. Please try signing in instead',
    'auth/weak-password': 'Password is too weak. Please choose a stronger password with at least 8 characters, including uppercase, lowercase, numbers, and special characters',
    'auth/invalid-email': 'Please enter a valid email address',
    'auth/user-disabled': 'This account has been disabled. Please contact support',
    'auth/too-many-requests': 'Too many failed attempts. Please wait a moment before trying again',
    'auth/network-request-failed': 'Network error. Please check your connection and try again',
    'auth/invalid-credential': 'Invalid credentials. Please check your email and password',
    'auth/requires-recent-login': 'Please sign in again to complete this action',
    'auth/credential-already-in-use': 'This credential is already associated with another account',
    'auth/invalid-verification-code': 'Invalid verification code. Please try again',
    'auth/invalid-verification-id': 'Invalid verification ID. Please request a new code',
    'auth/missing-verification-code': 'Please enter the verification code',
    'auth/missing-verification-id': 'Verification ID is missing. Please try again',
    'auth/code-expired': 'Verification code has expired. Please request a new one',
    'auth/session-expired': 'Your session has expired. Please sign in again',
    'auth/operation-not-allowed': 'This operation is not allowed. Please contact support',
    'auth/account-exists-with-different-credential': 'An account already exists with this email but different sign-in method',
    'auth/popup-closed-by-user': 'Sign-in popup was closed. Please try again',
    'auth/cancelled-popup-request': 'Sign-in was cancelled. Please try again',
    'auth/popup-blocked': 'Sign-in popup was blocked. Please allow popups and try again',
    'auth/unauthorized-domain': 'This domain is not authorized for sign-in. Please contact support',
    'auth/invalid-api-key': 'Invalid API key. Please contact support',
    'auth/app-deleted': 'This app has been deleted. Please contact support',
    'auth/expired-action-code': 'This action code has expired. Please request a new one',
    'auth/invalid-action-code': 'Invalid action code. Please check the link or request a new one',
    'auth/missing-email': 'Email address is required',
    'auth/missing-password': 'Password is required',
    'auth/quota-exceeded': 'Service quota exceeded. Please try again later',
    'auth/project-not-found': 'Project not found. Please contact support',
    'auth/insufficient-permission': 'Insufficient permissions. Please contact support',
    'auth/keychain-error': 'Keychain error. Please try again',
    'auth/internal-error': 'Internal error. Please try again later',
    'auth/invalid-user-token': 'Invalid user token. Please sign in again',
    'auth/user-token-expired': 'Your session has expired. Please sign in again',
    'auth/null-user': 'No user is currently signed in',
    'auth/app-not-authorized': 'This app is not authorized. Please contact support',
    'auth/invalid-credential': 'Invalid credentials. Please check your information',
    'auth/invalid-phone-number': 'Invalid phone number format',
    'auth/missing-phone-number': 'Phone number is required',
    'auth/quota-exceeded': 'SMS quota exceeded. Please try again later',
    'auth/captcha-check-failed': 'reCAPTCHA verification failed. Please try again',
    'auth/invalid-app-credential': 'Phone SMS is not configured for this app. Use email verification, or enable Phone Auth in Firebase (authorized domain + reCAPTCHA).',
    'auth/missing-app-credential': 'App credential is missing. Please contact support',
    'auth/invalid-verification-code': 'Invalid verification code. Please try again',
    'auth/invalid-verification-id': 'Invalid verification ID. Please try again',
    'auth/missing-verification-code': 'Verification code is required',
    'auth/missing-verification-id': 'Verification ID is required',
    'auth/phone-number-already-exists': 'This phone number is already registered',
    'auth/credential-already-in-use': 'This credential is already in use',
    'auth/timeout': 'Request timed out. Please try again',
    'auth/too-many-requests': 'Too many requests. Please wait before trying again'
  };

  // Firebase Firestore Error Codes Mapping
  static FIRESTORE_ERRORS = {
    'permission-denied': 'You do not have permission to perform this action',
    'not-found': 'The requested document was not found',
    'already-exists': 'A document with this ID already exists',
    'resource-exhausted': 'Service quota exceeded. Please try again later',
    'failed-precondition': 'Unable to connect. Please check your internet connection and try again',
    'aborted': 'The operation was aborted. Please try again',
    'out-of-range': 'The operation is out of range',
    'unimplemented': 'This operation is not implemented',
    'internal': 'Internal error. Please try again later',
    'unavailable': 'You are currently offline. Please check your internet connection and try again',
    'data-loss': 'Data loss occurred. Please contact support',
    'unauthenticated': 'You must be signed in to perform this action',
    'invalid-argument': 'Invalid argument provided',
    'deadline-exceeded': 'Request timed out. Please check your connection and try again',
    'cancelled': 'The operation was cancelled',
    'unknown': 'An unknown error occurred. Please try again'
  };

  // Network Error Types
  static NETWORK_ERRORS = {
    'NETWORK_ERROR': 'Network connection error. Please check your internet connection',
    'TIMEOUT': 'Request timed out. Please try again',
    'OFFLINE': 'You are currently offline. Please check your connection',
    'SERVER_ERROR': 'Server error. Please try again later',
    'CORS_ERROR': 'Cross-origin request blocked. Please contact support'
  };

  /**
   * Format Firebase Auth error for user display
   * @param {Error} error - The error object
   * @returns {string} User-friendly error message
   */
  static formatAuthError(error) {
    if (!error) return 'An unexpected error occurred';
    
    const errorCode = error.code || '';
    const errorMessage = error.message || '';
    
    // Check for specific Firebase Auth error codes
    if (this.FIREBASE_AUTH_ERRORS[errorCode]) {
      return this.FIREBASE_AUTH_ERRORS[errorCode];
    }
    
    // Check for password requirements error
    if (errorMessage.includes('password-does-not-meet-requirements') || 
        errorMessage.includes('Password must contain')) {
      return this.formatPasswordRequirementsError(errorMessage);
    }
    
    // Check for network errors
    if (errorCode.includes('network') || errorMessage.includes('network')) {
      return this.NETWORK_ERRORS.NETWORK_ERROR;
    }
    
    // Check for timeout errors
    if (errorCode.includes('timeout') || errorMessage.includes('timeout')) {
      return this.NETWORK_ERRORS.TIMEOUT;
    }
    
    // Return original message if no specific mapping found
    return errorMessage || 'An unexpected error occurred. Please try again';
  }

  /**
   * Format Firebase Firestore error for user display
   * @param {Error} error - The error object
   * @returns {string} User-friendly error message
   */
  static formatFirestoreError(error) {
    if (!error) return 'An unexpected error occurred';
    
    const errorCode = error.code || '';
    
    // Check for offline/unavailable errors first (these are network-related)
    if (errorCode === 'unavailable' || errorCode === 'failed-precondition') {
      // Check if we're actually offline
      if (typeof navigator !== 'undefined' && !navigator.onLine) {
        return this.NETWORK_ERRORS.OFFLINE;
      }
      // Otherwise treat as network error
      return this.NETWORK_ERRORS.NETWORK_ERROR;
    }
    
    // Check for timeout/deadline errors (network-related)
    if (errorCode === 'deadline-exceeded') {
      return this.NETWORK_ERRORS.TIMEOUT;
    }
    
    // Check for specific Firestore error codes
    if (this.FIRESTORE_ERRORS[errorCode]) {
      return this.FIRESTORE_ERRORS[errorCode];
    }
    
    // Check for permission errors
    if (errorCode.includes('permission') || error.message?.includes('permission')) {
      return this.FIRESTORE_ERRORS['permission-denied'];
    }
    
    // Check for network errors in error code or message
    if (errorCode.includes('network') || error.message?.includes('network')) {
      return this.NETWORK_ERRORS.NETWORK_ERROR;
    }
    
    // Return original message if no specific mapping found
    return error.message || 'An unexpected error occurred. Please try again';
  }

  /**
   * Format password requirements error
   * @param {string} errorMessage - The error message
   * @returns {string} User-friendly password requirements message
   */
  static formatPasswordRequirementsError(errorMessage) {
    const requirements = [];
    
    if (errorMessage.includes('Password must contain a lower case character')) {
      requirements.push('lowercase letter (a-z)');
    }
    if (errorMessage.includes('Password must contain an upper case character')) {
      requirements.push('uppercase letter (A-Z)');
    }
    if (errorMessage.includes('Password must contain a non-alphanumeric character')) {
      requirements.push('special character (!@#$%^&*)');
    }
    if (errorMessage.includes('Password must be at least')) {
      const match = errorMessage.match(/Password must be at least (\d+) characters/);
      if (match) {
        requirements.push(`at least ${match[1]} characters`);
      }
    }
    
    if (requirements.length > 0) {
      return `Password requirements not met. Please ensure your password includes: ${requirements.join(', ')}.`;
    }
    
    return 'Password does not meet requirements. Please choose a stronger password.';
  }

  /**
   * Format general error for user display
   * @param {Error} error - The error object
   * @returns {string} User-friendly error message
   */
  static formatError(error) {
    if (!error) return 'An unexpected error occurred';
    
    // Check if device is offline (check this first for network-related errors)
    const isOffline = typeof navigator !== 'undefined' && !navigator.onLine;
    
    // Check if it's a Firebase Auth error
    if (error.code && error.code.startsWith('auth/')) {
      return this.formatAuthError(error);
    }
    
    // Check if it's a Firestore error
    if (error.code && (error.code.startsWith('firestore/') || this.FIRESTORE_ERRORS[error.code])) {
      return this.formatFirestoreError(error);
    }
    
    // Check for offline state - if device is offline, prioritize offline message
    if (isOffline && (error.code === 'unavailable' || 
                      error.code === 'failed-precondition' ||
                      error.code === 'OFFLINE' ||
                      !error.code)) {
      return this.NETWORK_ERRORS.OFFLINE;
    }
    
    // Check for network errors in error code
    if (error.code) {
      if (error.code === 'unavailable' || error.code === 'failed-precondition') {
        return isOffline ? this.NETWORK_ERRORS.OFFLINE : this.NETWORK_ERRORS.NETWORK_ERROR;
      }
      if (error.code === 'OFFLINE' || error.code === 'NETWORK_ERROR') {
        return this.NETWORK_ERRORS.NETWORK_ERROR;
      }
      if (error.code.includes('network') || error.code.includes('timeout')) {
        return error.code.includes('timeout') ? this.NETWORK_ERRORS.TIMEOUT : this.NETWORK_ERRORS.NETWORK_ERROR;
      }
    }
    
    // Check for network errors in message
    if (error.message) {
      const lowerMessage = error.message.toLowerCase();
      if (lowerMessage.includes('network') || lowerMessage.includes('fetch failed')) {
        return isOffline ? this.NETWORK_ERRORS.OFFLINE : this.NETWORK_ERRORS.NETWORK_ERROR;
      }
      if (lowerMessage.includes('timeout')) {
        return this.NETWORK_ERRORS.TIMEOUT;
      }
      if (lowerMessage.includes('offline') || lowerMessage.includes('unavailable')) {
        return this.NETWORK_ERRORS.OFFLINE;
      }
    }
    
    // Return original message if no specific mapping found
    return error.message || 'An unexpected error occurred. Please try again';
  }

  /**
   * Log error for debugging (development only)
   * @param {Error} error - The error object
   * @param {string} context - Context where the error occurred
   */
  static logError(error, context = 'Unknown') {
    if (process.env.NODE_ENV === 'development') {
      console.error(`[${context}] Error:`, {
        message: error.message,
        code: error.code,
        stack: error.stack,
        timestamp: new Date().toISOString()
      });
    }
  }

  /**
   * Handle error with consistent formatting and logging
   * @param {Error} error - The error object
   * @param {string} context - Context where the error occurred
   * @returns {string} User-friendly error message
   */
  static handleError(error, context = 'Unknown') {
    this.logError(error, context);
    return this.formatError(error);
  }
}

export default ErrorHandler;




